import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../../../../shadcnComponents/ui/accordion";
import { Button } from "../../../../../shadcnComponents/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../../../shadcnComponents/ui/dialog";
import {
  ArrowRight,
  InfoIcon,
  MessageCircle,
  Repeat2,
  ThumbsUp,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Checkbox } from "../../../../../shadcnComponents/ui/checkbox";
import { Label } from "../../../../../shadcnComponents/ui/label";
import { cn } from "../../../../../lib/utils";
import {
  getRandomProfileEmoji,
  OpenInNewWindow,
} from "../../../../../Helpers/helper";
import { formatFollowersCount } from "../../../../../Helpers/format-helper";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../../../shadcnComponents/ui/tooltip";
import { RadioGroup, RadioGroupItem } from "@/shadcnComponents/ui/radio-group";
import { Textarea } from "@/shadcnComponents/ui/textarea";

export const CollabFormAccordion = ({
  Header,
  ContentArray,
  defaultValue,
}: {
  Header: React.ReactNode;
  ContentArray: React.ReactNode[];
  defaultValue?: string;
}) => {
  return (
    <>
      <Accordion
        type="single"
        defaultValue={defaultValue}
        collapsible
        className="w-full"
      >
        <AccordionItem className="w-full" value={defaultValue || "my-value"}>
          <AccordionTrigger className="w-full px-[17px] py-[10px] data-[state=closed]:rounded-[10px_10px_10px_10px] data-[state=open]:rounded-[10px_10px_0px_0px] bg-[#1B1F23] text-[#FFFFFF]">
            {Header}
          </AccordionTrigger>
          <AccordionContent className="w-full pb-0">
            <div className="flex w-full flex-col items-start gap-[22px] shrink-0 self-stretch bg-[#121518] px-[29px] py-[17px] rounded-[0px_0px_10px_10px] ">
              {ContentArray.map((content, index) => (
                <div className="w-full" key={index}>
                  {content}
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </>
  );
};

export const AccordionFormHeader = ({ title }: { title: string }) => {
  return <div>{title}</div>;
};

export const AcceptCollabDialog = ({
  TriggerButton,
  status,
  onClickAccept,
  collabType,
}: {
  TriggerButton: React.ReactNode;
  status: string;
  onClickAccept: (e, isAccepted) => void;
  collabType: string;
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isOpen, setIsOpen] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);

  const handleClickNextStep = () => {
    setCurrentStep((prev) => prev + 1);
  };

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
    }
  }, [isOpen]);

  useEffect(() => {
    if (status === "accepted") {
      setCurrentStep(3);
    }
  }, [status]);

  const handleAccept = (e) => {
    onClickAccept(e, isAccepted);
    setIsOpen(true);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{TriggerButton}</DialogTrigger>
      <DialogContent className="text-white border-none  shadow-[0px_10px_20px_0px_rgba(0,0,0,0.10)] rounded-lg bg-[#1B1F23] max-w-xl">
        {
          <>
            <>
              {currentStep === 1 && (
                <AcceptDialogStep1 handleClickNextStep={handleClickNextStep} />
              )}

              {currentStep === 2 && (
                <AcceptDialogStep2
                  onClickAccept={handleAccept}
                  setIsAccepted={setIsAccepted}
                  isAccepted={isAccepted}
                  collabType={collabType}
                />
              )}
            </>
            {/* ) : (
              <AcceptDialogStep3 onClickShareDraft={onClickShareDraft} />
            )} */}
          </>
        }
      </DialogContent>
    </Dialog>
  );
};

const AcceptDialogStep1 = ({ handleClickNextStep }) => {
  return (
    <>
      <div className="text-[#FFF] font-[Inter] text-base not-italic font-bold leading-[24px]">
        How Collaborations Work
      </div>

      <div className="grid gap-4 pb-3">
        <div className="flex flex-col items-start gap-4">
          <ol className="list-disc text-[#A3A3A3] font-[Inter] text-sm not-italic font-normal leading-[20px] space-y-1">
            <li className="ms-4">
              If the collaboration interests you, accept it.
              <i className="flex gap-1 ">
                <b>Tip:</b> Accepting more collaborations opens up further
                opportunities!
              </i>
            </li>
            <li className="ms-4">
              Submit your draft content as per the brand's requirements within
              24-48 hours.
            </li>
            <li className="ms-4">
              Wait for the brand’s feedback. Make any requested changes and
              resubmit.
            </li>
            <li className="ms-4">
              Once approved, check the campaign start date and make your post
              live. Any changes to the start date will be communicated.
            </li>
            <li className="ms-4">
              After the campaign ends, your earnings will be added to your
              account. You can withdraw them anytime.
            </li>
          </ol>
        </div>
      </div>

      <div className="w-full flex sm:justify-end">
        <Button
          variant="destructive"
          onClick={handleClickNextStep}
          type="button"
          className="gap-3 min-w-[180px]"
        >
          Next
          <ArrowRight size={16} />
        </Button>
      </div>
    </>
  );
};

const AcceptDialogStep2 = ({
  onClickAccept,
  setIsAccepted,
  isAccepted,
  collabType,
}) => {
  const performanceBasedPayments = (
    <ol className="list-disc text-[#A3A3A3] font-[Inter] text-sm not-italic font-normal leading-[20px] space-y-1">
      <li className="ms-4">
        <b>Performance-Based Earnings:</b> Your payments are based on how well
        your post performs. The earnings mentioned in collaboration briefs are
        estimates, calculated from AI-predicted performance levels.
      </li>
      <li className="ms-4">
        <b>Indicators of Good Performance:</b> High reach and quality engagement
        are key among other factors. If your post performs better than predicted
        levels, your earnings reflect that, based on the max budget permitted by
        the brand.
      </li>
      <li className="ms-4">
        <b>Adjustments for Performance Fluctuations:</b> If a campaign
        underperforms, earnings may be adjusted but rest assured, they will
        never drop below a minimum threshold to protect your efforts.
      </li>
      <li className="ms-4">
        <b>Transparency and Fairness:</b> You'll have access to the same metrics
        as brands, ensuring there's no misinformation. Any major campaign
        changes—like budget or live date—will be communicated to you promptly.
      </li>
      <p>
        This approach is designed to ensure that you are fairly compensated for
        your creativity and hard work!
      </p>
    </ol>
  );

  const fixedBasedPayments = (
    <ol className="list-disc text-[#A3A3A3] font-[Inter] text-sm not-italic font-normal leading-[20px] space-y-1">
      <li className="ms-4">
        <b>Fixed Payment Model:</b> Your payout for this collaboration is set as
        shown in the collaboration brief and will not change based on
        performance. The amount has been decided by the brand.
      </li>
      <li className="ms-4">
        <b>No Adjustments for Performance:</b> Unlike our performance-based
        model, your payout remains unchanged regardless of how your post
        performs. The agreed-upon amount is guaranteed as long as all campaign
        guidelines are met.
      </li>
      <li className="ms-4">
        <b>Transparency and Fairness:</b> You'll have access to the same metrics
        as brands, ensuring clarity throughout the campaign. If there are any
        major changes to campaign parameters—like duration or budget— these will
        be communicated to you promptly.
      </li>
      <p>
        This structure ensures certainty in your earnings so you can focus on
        creating without worrying about fluctuations in payment.
      </p>
    </ol>
  );

  return (
    <>
      <div className="text-[#FFF] font-[Inter] text-base not-italic font-bold leading-[24px]">
        Understanding Your Payment Structure
      </div>

      <div className="grid gap-4 pb-3">
        <div className="flex flex-col items-start gap-4">
          {collabType === "fixed"
            ? fixedBasedPayments
            : performanceBasedPayments}
          <div className="flex gap-2">
            <Checkbox
              id="agree"
              checked={isAccepted}
              onCheckedChange={(value) => setIsAccepted(!!value)}
              className="border-[#FFF]"
            />
            <Label
              htmlFor="agree"
              className="text-[#FFF] text-sm not-italic font-medium leading-[14px] cursor-pointer"
            >
              I have read and understand how the payment structure works
            </Label>
          </div>
        </div>
      </div>

      <div className="w-full flex sm:justify-end">
        <Button
          // type="submit"
          variant="destructive"
          onClick={(e) => onClickAccept(e, isAccepted)}
          className="gap-3 min-w-[180px]"
          disabled={!isAccepted}
        >
          Accept
          <ArrowRight size={16} />
        </Button>
      </div>
    </>
  );
};

export const ShareDraftDialog = ({
  onClickShareDraft,
}: {
  onClickShareDraft: () => void;
}) => {
  return (
    <>
      <Dialog open={true}>
        <DialogTrigger>Open</DialogTrigger>
        <DialogContent className="text-white border-none  shadow-[0px_10px_20px_0px_rgba(0,0,0,0.10)] rounded-lg bg-[#1B1F23] max-w-xl">
          <AcceptDialogStep3 onClickShareDraft={onClickShareDraft} />
        </DialogContent>
      </Dialog>
    </>
  );
};

const AcceptDialogStep3 = ({ onClickShareDraft }) => {
  return (
    <>
      <div className="grid gap-4 pb-3">
        <div className="flex flex-col items-start gap-4">
          <div className="flex flex-col gap-1 text-[rgba(255,255,255,0.80)]">
            <div className="w-full flex justify-center">
              {/* <CheckCircle2 size={70} color="#4ECB71" /> */}
              <span className="text-5xl">🎉</span>
            </div>
            <h1 className="text-center text-2xl not-italic font-bold leading-[normal]">
              Congratulations! <br /> You Accepted the Collaboration
            </h1>
            <h3 className="text-center text-sm not-italic font-normal leading-[normal] px-4">
              Now continue to draft the content as per the brand's requirements
              within given time.
            </h3>
          </div>
        </div>
      </div>
      <div className="w-full flex sm:justify-center ">
        <DialogClose asChild>
          <Button
            variant="destructive"
            onClick={onClickShareDraft}
            type="submit"
          >
            Share Draft
          </Button>
        </DialogClose>
      </div>
    </>
  );
};

export const DeclineCollabDialog = ({
  onClickDecline,
  Trigger,
  declineReason,
  setDeclineReason,
  description,
  setDescription,
}: {
  onClickDecline: (e) => void;
  Trigger: React.ReactNode;
  declineReason: string;
  setDeclineReason: (reason: string) => void;
  description: string;
  setDescription: (description: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>{Trigger}</DialogTrigger>
        <DialogContent className="text-white border-none  shadow-[0px_10px_20px_0px_rgba(0,0,0,0.10)] rounded-lg  bg-[#1B1F23] max-w-xl">
          <div className="text-[#FFF] font-[Inter] text-base not-italic font-bold leading-[24px]">
            Decline Collaborations!
          </div>
          <div className="grid gap-4 pb-4">
            <div className="flex flex-col items-start gap-4">
              <ol className="list-disc text-[#A3A3A3] font-[Inter] text-sm not-italic font-normal leading-[20px] space-y-1">
                <li className="ms-4">
                  Accept the collaboration if you like to do this collaboration(
                  I would suggest to accept as this will open more opportunity )
                </li>
                <li className="ms-4">
                  After acceptance, share draft content as per the requirement
                  with in 48 hrs
                </li>
                <li className="ms-4">
                  Wait for the brands approval, if there are any changes please
                  do that & Submit changes again
                </li>
                <li className="ms-4">
                  once brands approved, check the start campaign date & make it
                  live, any changes on the start date will be communicated as
                  well
                </li>
                <li className="ms-4">
                  after the campaign periods end, respective amount of earning
                  will be added in your account, you can withdraw anytime
                </li>
              </ol>
            </div>
          </div>
          <DialogFooter className="w-full flex sm:justify-center">
            <DialogClose asChild>
              <Button
                type="button"
                onClick={() => {
                  setOpen(false);
                  setIsDeclineModalOpen(true);
                }}
                className="bg-transparent text-white"
                variant="outline"
              >
                Decline Collaboration
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={isDeclineModalOpen} onOpenChange={setIsDeclineModalOpen}>
        <DialogContent className="sm:max-w-[425px] bg-[#1B1F23] text-[#fff] border-none  shadow-[0px_10px_20px_0px_rgba(0,0,0,0.10)]  ">
          <DialogHeader>
            <DialogTitle>Reason for Decline</DialogTitle>
            <DialogDescription className="text-red-500">
              Important note - we would suggest you not to decline as it might
              impact the other collaboration
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4 text-[#A3A3A3]">
            <RadioGroup value={declineReason} onValueChange={setDeclineReason}>
              {["charges", "availability", "brand-fit", "other"].map(
                (value) => (
                  <div key={value} className="flex items-center space-x-2">
                    <RadioGroupItem
                      value={value}
                      id={value}
                      className="border-white text-white data-[state=checked]:bg-white data-[state=checked]:text-[#1B1F23]"
                    />
                    <Label htmlFor={value} className="capitalize">
                      {value.replace("-", " ")}
                    </Label>
                  </div>
                )
              )}
            </RadioGroup>
            <div className="grid gap-2">
              <Label htmlFor="description">
                Please describe so we will make sure from next time
              </Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="h-20 bg-transparent "
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              type="button"
              onClick={(e) => {
                setIsDeclineModalOpen(false);
                onClickDecline(e);
              }}
              className="bg-transparent text-white"
            >
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const DraftStatusHolder = ({
  status,
  title,
  variant = "pending",
}: {
  status: string | React.ReactNode;
  title: string;
  variant?: "pending" | "approved";
}) => {
  const styles = {
    pending: "text-[#5E1111] bg-[#D75636]",
    approved: "text-[#115E59] bg-[#36D7CC]",
  };
  return (
    <div className="grid grid-cols-2 gap-5 py-2 px-5 border rounded-lg border-[#1B1F23] items-center">
      <div className="text-[#FFF] font-[Inter] text-sm not-italic font-semibold leading-[24px]">
        {title}
      </div>
      <div
        className={cn(
          "flex gap-2 items-center px-4 py-1 rounded-lg font-[Inter] text-sm not-italic font-semibold leading-[24px] uppercase",
          {
            [styles.pending]: variant === "pending",
            [styles.approved]: variant === "approved",
          }
        )}
      >
        {status}
      </div>
    </div>
  );
};

interface TopPostCardProps {
  profile: string;
  name: string;
  postDescription: string;
  likes?: number | string;
  comments?: number | string;
  reposts?: number | string;
  postLink?: string;
  tagline?: string;
}

export const TopPostCard = ({
  profile,
  name,
  postDescription,
  likes,
  comments,
  reposts,
  postLink,
  tagline,
}: TopPostCardProps) => {
  return (
    <>
      <div className="min-w-[270px] max-w-[320px] p-4 flex flex-col bg-[#1B1F23]  gap-[20px] rounded-[10px] justify-between">
        <div className="flex flex-col gap-[20px] mb-3">
          <div className="w-full items-center justify-start flex gap-2 ">
            <div className="overflow-hidden min-w-[40px] min-h-[40px]  rounded-full  ">
              <img
                className="w-[40px] h-[40px] cover "
                src={profile}
                alt=""
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onError={(e: any) => {
                  const randomProfileEmoji = getRandomProfileEmoji();
                  e.target.onerror = null;
                  e.target.src = randomProfileEmoji;
                }}
              />
            </div>
            <div className="w-full flex flex-col gap-1">
              <span className=" text-xs font-semibold leading-[14.52px] text-left">
                {name}
              </span>
              <span className="text-xs text-[#949698] font-inter font-light text-left line-clamp-1 ">
                {tagline}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap flex-col gap-1 ">
            <div className="text-[#C7C3C3] font-[Inter] text-[12px] not-italic font-normal leading-[22px] break-all line-clamp-[9]">
              {postDescription}
            </div>
            <span
              onClick={() => OpenInNewWindow({ link: postLink })}
              className=" cursor-pointer text-xs font-bold leading-[14.52px] text-left text-[#666666] line-clamp-4"
            >
              view more
            </span>
          </div>
        </div>

        <div className=" flex gap-[20px] ">
          {[
            {
              icon: <ThumbsUp size={20} color="#AFB1B2" />,
              key: "Avg. Engagement",
              value: `${formatFollowersCount(likes) ?? "NA"}`,
            },
            {
              icon: <MessageCircle size={20} color="#AFB1B2" />,
              key: "Avg. Likes",
              value: `${formatFollowersCount(comments) ?? "NA"}`,
            },
            {
              icon: <Repeat2 size={20} color="#AFB1B2" />,
              key: "Avg. Comments",
              value: `${formatFollowersCount(reposts) ?? "NA"}`,
            },
          ]?.map((item, i) => {
            if (item?.value === "NA" || parseInt(item?.value) <= 0) return null;
            return (
              <div
                key={i}
                className="flex gap-1 items-center  @apply text-[12px] font-normal leading-[14.52px] text-left text-[#AFB1B2]"
              >
                {item.icon}
                {item.value}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

interface MetricsCardProps {
  icon?: React.ReactNode | string;
  value: string | number;
  title: string;
  tooltip?: React.ReactNode;
}

export const MetricsCard: React.FC<MetricsCardProps> = ({
  icon,
  value,
  title,
  tooltip,
}) => {
  return (
    <div className={`w-full flex p-[22px] rounded-[10px] bg-[#1B1F23] h-full`}>
      <div className="flex items-center gap-[12px]">
        {icon && <div className="flex items-center justify-center">{icon}</div>}
        <div className="flex flex-col">
          <span className="text-[#FFF] font-[Inter] text-[20px] not-italic font-bold leading-[normal]">
            {value ?? "-"}
          </span>
          <span className="text-[#EEE] cursor-pointer flex items-center gap-1 justify-center font-[Inter] text-[12px] not-italic font-normal leading-[normal]">
            {title}
            {tooltip && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <InfoIcon size={15} />
                  </TooltipTrigger>
                  <TooltipContent className="bg-current border-none ">
                    {tooltip}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </span>
        </div>
      </div>
    </div>
  );
};
